import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { httpRequest } from "../../../../services/http";
import store from "../../../../state/store";
import Events from "../../../../common/helpers/event-helpers/events";
import Loader from "../../../../components/Loader";
import LoginModal from "../../../../components/LoginModal";
import AddToast from "../../../../state/actions/AddToast";
import Modal from "../../../../components/atoms/Modal";
import RequestError from "../../../../components/atoms/RequestError";
import {
  getPathogenicityOptionFromShortName,
  getAdditionalOptionsFromAdditionalInfo,
  noFlagSelectedOrLimitReached,
} from "./helpers";
import getTotalDiseasesAndPhenotypes from "../../../../common/helpers/getTotalDiseasesAndPhenotypes";
import Controls from "./Controls";
import ClassSelector from "./ClassSelector";
import CommentBox from "./CommentBox";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import Button from "./Button";
import SuggestedPhenotypes from "./SuggestedPhenotypes";
import { apiUrl, varsomeHostUrl } from "../../../../common/helpers/Urls";
import {
  getUserOrganization,
  getUserOrganizationId,
  getVariantGenes,
  getUserOrganizationPublic,
} from "../../../../state/selectors/general.selectors";
import isUserProfilePublic from "../../../../common/helpers/isUserProfilePublic";
import { eventTrack } from "../../../../common/helpers/helpers";
import HPOScreen from "./HPOScreen";
import useSuggestedData from "./useSuggestedPhenotypes";

const ClassificationModal = (props) => {
  const {
    variantGenes,
    element,
    editLinkText,
    addLinkText,
    userFlag,
    addToHelpTour,
    variantId,
    variantClassesList,
    acmgVerdict,
    reduxVariantId,
    analyticsLocation,
    userOrganisation,
    userOrganisationId,
    userOrganisationPublic,
  } = props;

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [flag, setFlag] = useState();
  const [addInfo, setAddInfo] = useState([]);
  const [hpo, setHpo] = useState([
    { source: "community", diseases: [], phenotypes: [] },
  ]);
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);
  const [hideUser, setHideUser] = useState(false);
  const [totalDiseasesAndPhenotypes, setTotalDiseasesAndPhenotypes] =
    useState(0);
  const { suggestedPhenotypes, hasSuggested, genes } =
    useSuggestedData(variantGenes);

  const toggleModal = () => {
    if (userFlag) {
      const pathogenicity = getPathogenicityOptionFromShortName(
        userFlag.flag,
        variantClassesList
      );
      const hu = !userFlag.user__screen_name;
      const c = userFlag.comment || "";
      const jsonData = userFlag.json_data;
      const hpoCopy = [...hpo];
      hpoCopy[0].diseases = jsonData.diseases.map((disease) => {
        disease.checked = true;
        if (Array.isArray(disease.phenotypes)) {
          disease.expanded = true;
          disease.phenotypes = disease.phenotypes.map((phen) => {
            phen.checked = true;
            return phen;
          });
        }
        return disease;
      });
      hpoCopy[0].phenotypes = jsonData.phenotypes.map((phen) => {
        phen.checked = true;
        return phen;
      });
      const addinf = getAdditionalOptionsFromAdditionalInfo(
        jsonData.additional_info
      );
      const total = getTotalDiseasesAndPhenotypes(hpoCopy);
      setFlag(pathogenicity);
      setHpo(hpoCopy);
      setAddInfo(addinf);
      setComment(c);
      setTotalDiseasesAndPhenotypes(total);
      setModalOpen(!modalOpen);
      setHideUser(hu);
      return;
    }
    setModalOpen(!modalOpen);
  };

  useEffect(() => {
    if (modalOpen) {
      if (analyticsLocation) {
        eventTrack(analyticsLocation, "Classify Variant", "");
      }
    }
    return () => {};
  }, [modalOpen]);

  const saveEntry = (del) => {
    const url = `${apiUrl}/flag/${variantId || reduxVariantId}`;
    setError(null);

    /* Add to diseases array the selected diseases. */
    /* If disease has associated phenotypes checked, we should sent them along with the disease. */
    const hpoCopy = [...hpo];
    let diseases = hpoCopy[0].diseases
      .map((disease) => ({
        ...disease,
        phenotypes: (disease?.phenotypes ?? [])
          .map((phen) => ({ ...phen }))
          .filter((phen) => phen.checked),
      }))
      .filter((dis) => dis.checked);

    /* Add to diseases array the checked suggested diseases. */
    const suggestedPhenotypesCopy = [...suggestedPhenotypes];
    const suggestedDiseases = hasSuggested
      ? suggestedPhenotypesCopy[0].diseases
          .map((disease) => ({
            ...disease,
            phenotypes: (disease?.phenotypes ?? [])
              .map((phen) => ({ ...phen }))
              .filter((phen) => phen.checked),
          }))
          .filter((dis) => dis.checked)
      : [];
    diseases = diseases.concat(suggestedDiseases);

    /* Add to phenotypes array the selected phenotypes. */
    let phenotypes = hpoCopy[0]?.phenotypes
      .map((phen) => ({ ...phen }))
      .filter((phen) => phen.checked);

    /* Add to phenotypes array the checked suggested phenotypes. */
    const suggestedPhenos = hasSuggested
      ? (suggestedPhenotypesCopy[0].phenotypes ?? [])
          .map((phen) => ({ ...phen }))
          .filter((phen) => phen.checked)
      : [];
    phenotypes = phenotypes.concat(suggestedPhenos);

    const additionalInfo = addInfo ? addInfo.map((info) => info.value) : [];

    const data = {
      flag: flag.value,
      hide_user: hideUser,
      comment,
      json_data: {
        diseases,
        phenotypes,
        additional_info: additionalInfo,
      },
    };

    /* Handle organisation */
    if ((userOrganisationId || userOrganisation) && userOrganisationPublic) {
      data.json_data.userOrganisation = userOrganisation;
      data.org = userOrganisationId || null;
    } else {
      data.org = null;
    }

    /* Send the current ACMG annotation to compare with the user's classification. */
    if (acmgVerdict) {
      /* Get ACMG verdict(e.g. benign) and find the short code(e.g. B). */
      const { ACMG_rules: acmgRules } = acmgVerdict;
      const { verdict } = acmgRules;
      for (let i = 0; i < variantClassesList.length; i++) {
        const vc = variantClassesList[i];
        if (vc.name === verdict) {
          data.acmg_class = vc.short_name;
          break;
        }
      }
    }

    let method = "post";
    if (userFlag) {
      method = del ? "delete" : "put";
    }
    setLoading(true);
    httpRequest({
      method,
      url,
      data: method !== "delete" ? data : null,
    })
      .then((response) => {
        const toastText = del
          ? "Classification was removed"
          : "Classification was saved successfully";
        AddToast(toastText, "success");
        toggleModal();
        const variantFlags = response.data;
        let uFlag;
        if (Array.isArray(variantFlags) && variantFlags.length > 0) {
          for (let i = variantFlags.length - 1; i >= 0; i--) {
            if (variantFlags[i].user_flag) {
              uFlag = variantFlags[i];
              break;
            }
          }
        }
        store.dispatch({
          type: "SET_VARIANT_FLAGS",
          variantFlags,
          userFlag: uFlag,
        });
        Events.dispatch("FLAG_CHANGED");
      })
      .catch((err) => {
        console.error("Error saving flag ", err);
        setLoading(false);
        setError("Error saving this classification. Please contact support.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateNumberOfSelections = () => {
    const totalSuggested = suggestedPhenotypes
      ? getTotalDiseasesAndPhenotypes(suggestedPhenotypes[0])
      : 0;
    const total = getTotalDiseasesAndPhenotypes(hpo[0]);
    setTotalDiseasesAndPhenotypes(total + totalSuggested);
  };

  const getDiseaseWithPhenotypes = (disease) => {
    const diseaseId = disease?.disease_id;
    const url = `${apiUrl}/disease_with_phenotypes/${diseaseId}`;
    httpRequest
      .get(url)
      .then((response) => {
        const dis = response.data;
        dis.checked = true;
        dis.expanded = true;
        dis.omim_id = disease[0]?.omim_id || "";
        const h = [...hpo];
        h[0].diseases.unshift(dis);
        setHpo(h);
      })
      .catch((err) => {
        console.error(
          "Error fetching disease with phenotypes for this disease_id ",
          err
        );
      });
  };

  const addDisease = (disease) => {
    if (disease) {
      getDiseaseWithPhenotypes(disease);
    }
  };

  const addPhenotype = (phenotype) => {
    if (phenotype) {
      phenotype.checked = true;
      phenotype.phenotype_id = phenotype.id;
      const h = [...hpo];
      h[0].phenotypes.unshift(phenotype);
      setHpo(h);
    }
  };

  const flagPropertyChanged = (item) => {
    setFlag(item);
  };

  const infoPropertyChanged = (item) => {
    setAddInfo(item);
  };

  const commentChanged = (c) => {
    setComment(c);
  };

  const toggleLoginModal = () => {
    setShowLoginModal(!showLoginModal);
  };

  const modalContent = isUserProfilePublic ? (
    <React.Fragment>
      {loading && (
        <div style={{ textAlign: "center" }}>
          <Loader size="small" />
        </div>
      )}
      {!loading && (
        <div className="varsome-modal-content">
          {error && <RequestError error={error} />}
          <ClassSelector
            flag={flag}
            additionalInfo={addInfo}
            flagPropertyChanged={flagPropertyChanged}
            infoPropertyChanged={infoPropertyChanged}
          />
          <HPOScreen
            hpo={hpo[0]}
            numberOfHPOSelections={totalDiseasesAndPhenotypes}
            addDisease={(d) => addDisease(d)}
            addPhenotype={(p) => addPhenotype(p)}
            updateNumberOfSelections={updateNumberOfSelections}
          />
          {hasSuggested && !userFlag && (
            <SuggestedPhenotypes
              suggestedPhenotypes={suggestedPhenotypes}
              genes={genes}
              updateNumberOfSelections={updateNumberOfSelections}
            />
          )}
          <CommentBox comment={comment} commentChanged={commentChanged} />
        </div>
      )}
    </React.Fragment>
  ) : (
    <div className="varsome-modal-content">
      To contribute, please enable the
      <b> Allow other users to view your full name</b> in your
      <a href={`${varsomeHostUrl}/account-settings/`}> Account settings </a>.
    </div>
  );

  return (
    <span>
      <Button
        element={element}
        editLinkText={editLinkText}
        addLinkText={addLinkText}
        userFlag={userFlag}
        addToHelpTour={addToHelpTour}
        toggleLoginModal={toggleLoginModal}
        toggleModal={toggleModal}
      />
      <Modal
        isOpen={modalOpen || false}
        onRequestClose={toggleModal}
        ContentRender={() => (
          <div
            className={`tw-max-w-7xl tw-w-90screen ${
              isUserProfilePublic ? " flag-form " : ""
            }`}
          >
            <div
              className="tw-overflow-auto"
              style={{ height: "700px", maxHeight: "calc(80vh - 100px)" }}
            >
              <div className="tw-p-4 tw-text-lg">
                {userFlag
                  ? "Edit your classification"
                  : "Classify this variant"}
              </div>
              <div className="tw-flex-grow">{modalContent}</div>
            </div>
            <div className="tw-z-10">
              {isUserProfilePublic
                ? !loading && (
                    <Controls
                      userFlag={userFlag}
                      buttonDisabled={
                        noFlagSelectedOrLimitReached(
                          flag,
                          totalDiseasesAndPhenotypes
                        ) || !(comment && comment.length > 49)
                      }
                      saveEntry={saveEntry}
                    />
                  )
                : null}
            </div>
          </div>
        )}
      />
      <LoginModal showModal={showLoginModal} toggleModal={toggleLoginModal} />
    </span>
  );
};

ClassificationModal.propTypes = {
  element: PropTypes.element,
  variantId: PropTypes.string,
  editLinkText: PropTypes.bool,
  addLinkText: PropTypes.bool,
  userFlag: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ]),
  addToHelpTour: PropTypes.bool,
  variantGenes: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string,
    })
  ),
  variantClassesList: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      description: PropTypes.string,
      hideForCommon: PropTypes.bool,
      name: PropTypes.string,
      short_name: PropTypes.string,
    })
  ),
  acmgVerdict: PropTypes.shape({
    ACMG_rules: PropTypes.shape({
      approx_score: PropTypes.number,
      benign_subscore: PropTypes.string,
      clinical_score: PropTypes.number,
      pathogenic_subscore: PropTypes.string,
      verdict: PropTypes.string,
    }),
    classifications: PropTypes.arrayOf(PropTypes.string),
  }),
  reduxVariantId: PropTypes.string,
  analyticsLocation: PropTypes.string,
  userOrganisation: PropTypes.string,
  userOrganisationId: PropTypes.string,
  userOrganisationPublic: PropTypes.bool,
};

ClassificationModal.defaultProps = {
  element: null,
  variantId: null,
  editLinkText: false,
  addLinkText: false,
  userFlag: null,
  addToHelpTour: false,
  variantGenes: null,
  variantClassesList: null,
  acmgVerdict: null,
  reduxVariantId: null,
  analyticsLocation: null,
  userOrganisation: null,
  userOrganisationId: null,
  userOrganisationPublic: null,
};

const mapStateToProps = (state) => {
  return {
    variantGenes: getVariantGenes(state),
    variantClassesList: state?.variantClassesList,
    acmgVerdict: state?.variantResult?.acmg_annotation?.verdict,
    reduxVariantId: state?.variantResult?.variant_id,
    userOrganisation: getUserOrganization(state),
    userOrganisationId: getUserOrganizationId(state),
    userOrganisationPublic: getUserOrganizationPublic(state),
  };
};

const guarded = withErrorGuard(
  ClassificationModal,
  "ClassificationModal",
  "ClassificationModal"
);

export default connect(mapStateToProps, {})(guarded);
